import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ShoppingBag, Loader2, Link2, Unlink, RefreshCw, Copy, Info, Activity, UploadCloud, CheckCircle2, Tags, Menu } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PillSwitch } from "@/components/PillSwitch";

export default function Shopify() {
  const qc = useQueryClient();
  const [settings, setSettings] = useState(null);
  const [domain, setDomain] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [secret, setSecret] = useState("");
  const [locationId, setLocationId] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [pinging, setPinging] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [autolist, setAutolist] = useState(true);
  const [listingAll, setListingAll] = useState(false);
  const [qtySyncing, setQtySyncing] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [buildingMenu, setBuildingMenu] = useState(false);
  const [deduping, setDeduping] = useState(false);

  const { data: sales = [] } = useQuery({ queryKey: ["sales"], queryFn: async () => (await api.get("/sales")).data });
  const synced = sales.filter((s) => s.synced_platforms?.length);
  const connected = settings?.shopify?.connected;
  const { data: audit = [], refetch: refetchAudit } = useQuery({
    queryKey: ["shopify-audit"],
    queryFn: async () => (await api.get("/shopify/audit")).data,
    enabled: !!connected,
    refetchInterval: 30000,
  });

  const load = async () => {
    const { data } = await api.get("/settings");
    setSettings(data);
    setDomain(data.shopify.shop_domain || "");
    setLocationId(data.shopify.location_id || "");
    setAutolist(data.shopify_autolist !== false);
  };

  useEffect(() => { load(); }, []);

  const connect = async () => {
    if (!domain.trim()) { toast.error("Store domain is required"); return; }
    const hasCreds = clientId.trim() && clientSecret.trim();
    if (!connected && !hasCreds) { toast.error("Client ID and Client Secret are required"); return; }
    setConnecting(true);
    try {
      const { data } = await api.post("/settings/shopify/connect", {
        shop_domain: domain.trim(), client_id: clientId.trim(), client_secret: clientSecret.trim(),
        location_id: locationId.trim(), webhook_secret: secret.trim(),
      });
      toast.success(`Connected to ${data.shop_name || "Shopify"}`);
      if (data.webhook_registered) toast.success("Live order webhook registered");
      else if (data.webhook_error) toast.warning(`Connected, but webhook not registered: ${data.webhook_error}`);
      if (data.location_error) toast.warning("Connected, but no location detected — add the read_locations scope to your Shopify app and reconnect (or enter a Location ID manually).");
      setClientId(""); setClientSecret(""); setSecret("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    try { await api.post("/settings/shopify/disconnect"); toast.success("Shopify disconnected"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/shopify/sync");
      toast.success(`Synced: ${data.imported} new, ${data.updated} linked (${data.total} variants)`);
      qc.invalidateQueries({ queryKey: ["cards"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      refetchAudit();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSyncing(false);
    }
  };

  const reRegister = async () => {
    setRegistering(true);
    try {
      await api.post("/shopify/webhook/register");
      toast.success("Order webhook re-registered");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setRegistering(false);
    }
  };

  const ping = async () => {
    setPinging(true);
    try {
      const { data } = await api.post("/shopify/ping");
      toast.success(`Connected to ${data.shop_name || "Shopify"} · webhook ${data.webhook_registered ? "active" : "missing"}`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPinging(false); }
  };

  const pushUp = async () => {
    setPushing(true);
    try {
      const { data } = await api.post("/shopify/push-up");
      toast.success(`Pushed ${data.created} new${data.linked ? `, linked ${data.linked} existing` : ""}${data.failed ? `, ${data.failed} failed` : ""}${data.remaining ? ` · ${data.remaining} remaining` : ""}`);
      if (data.errors?.length) toast.error(data.errors[0]);
      await load();
      refetchAudit();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPushing(false); }
  };

  const toggleAutolist = async (val) => {
    setAutolist(val);
    try {
      await api.post("/settings/shopify/autolist", { enabled: val });
      toast.success(val ? "New cards will auto-list to Shopify" : "Auto-listing turned off");
    } catch (e) {
      setAutolist(!val);
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const listAll = async () => {
    setListingAll(true);
    try {
      let total = 0, linkedTotal = 0;
      let { data } = await api.post("/shopify/list-all");
      total += data.created; linkedTotal += (data.linked || 0);
      if (data.location_missing) toast.warning("No Shopify location set — add the read_locations scope and reconnect so stock can be set.");
      while (data.remaining > 0) {
        ({ data } = await api.post("/shopify/push-up"));
        total += data.created; linkedTotal += (data.linked || 0);
      }
      toast.success(`Listed ${total} new card(s)${linkedTotal ? `, linked ${linkedTotal} already on Shopify` : ""}`);
      refetchAudit();
      qc.invalidateQueries({ queryKey: ["cards"] });
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setListingAll(false); }
  };

  const pushQuantities = async () => {
    setQtySyncing(true);
    try {
      let after = "", synced = 0, failed = 0, done = false, warnedLoc = false;
      while (!done) {
        const { data } = await api.post("/shopify/push-quantities", null, { params: { after } });
        synced += data.synced; failed += data.failed; after = data.last_id; done = data.done;
        if (data.location_missing && !warnedLoc) { warnedLoc = true; toast.warning("No Shopify location set — add the read_locations scope and reconnect."); }
        if (!data.last_id && !done) break;
      }
      toast.success(`Synced ${synced} card quantit${synced === 1 ? "y" : "ies"}/price to Shopify${failed ? `, ${failed} failed` : ""}`);
      refetchAudit();
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setQtySyncing(false); }
  };

  const backfillCollections = async () => {
    setBackfilling(true);
    try {
      let after = "", tagged = 0, failed = 0, done = false;
      while (!done) {
        const { data } = await api.post("/shopify/collections/backfill", null, { params: { after } });
        tagged += data.tagged; failed += data.failed; after = data.last_id; done = data.done;
        if (!data.last_id && !done) break;
      }
      toast.success(`Grouped ${tagged} listing(s) into set collections${failed ? `, ${failed} failed` : ""}`);
      await load();
      refetchAudit();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBackfilling(false); }
  };

  const buildMenu = async () => {
    setBuildingMenu(true);
    try {
      const { data } = await api.post("/shopify/menu/build");
      toast.success(`Built "Shop by Set" menu — ${data.games} game(s), ${data.sets} set(s). Assign it in Shopify → Online Store → Navigation.`);
      refetchAudit();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBuildingMenu(false); }
  };

  const dedupe = async () => {
    setDeduping(true);
    try {
      let removedTotal = 0, relinkedTotal = 0, groups = 0, guard = 0;
      let { data } = await api.post("/shopify/dedupe");
      removedTotal += data.removed; relinkedTotal += data.relinked; groups = data.groups;
      while (data.remaining > 0 && data.removed > 0 && guard < 50) {
        guard++;
        ({ data } = await api.post("/shopify/dedupe"));
        removedTotal += data.removed; relinkedTotal += data.relinked;
      }
      if (groups === 0) toast.success("No duplicate listings found — your store is clean.");
      else toast.success(`Removed ${removedTotal} duplicate listing(s) across ${groups} card(s)${relinkedTotal ? `, relinked ${relinkedTotal}` : ""}`);
      qc.invalidateQueries({ queryKey: ["cards"] });
      refetchAudit();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDeduping(false); }
  };

  const retryFailed = async (action) => {
    if (action === "push_quantities") return pushQuantities();
    if (action === "collections_backfill") return backfillCollections();
    // push / list — re-run the push-up loop to retry items that failed to list
    setPushing(true);
    try {
      let listed = 0, failed = 0, guard = 0;
      let { data } = await api.post("/shopify/push-up");
      listed += data.created; failed = data.failed;
      while (data.remaining > 0 && guard < 100) {
        guard++;
        ({ data } = await api.post("/shopify/push-up"));
        listed += data.created; failed = data.failed;
      }
      toast.success(`Retried — ${listed} listed${failed ? `, ${failed} still failing` : ""}`);
      await load();
      refetchAudit();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPushing(false); }
  };

  const webhookUrl = settings?.shopify?.webhook_url || "";

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-6">
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-100">Shopify</h1>
        <p className="text-sm text-slate-400 mt-1">Connect your store, sync products and keep inventory in lock-step. A sale on one channel decrements stock on the others.</p>
      </div>

      <div className="rounded-2xl border p-6" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-emerald-950/80 border border-emerald-800/60 flex items-center justify-center"><ShoppingBag className="h-5 w-5 text-emerald-400" /></div>
            <div>
              <h2 className="font-heading text-lg font-bold text-slate-100">Store connection</h2>
              <div className={`text-xs font-medium flex items-center gap-1.5 ${connected ? "text-emerald-400" : "text-slate-500"}`}>
                <span className={`h-2 w-2 rounded-full ${connected ? "bg-emerald-400" : "bg-slate-600"}`} />
                {connected ? "Connected" : "Not connected"}
              </div>
            </div>
          </div>
          {connected && (
            <button onClick={sync} disabled={syncing} data-testid="trigger-manual-sync-button"
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500 text-[#1a1205] text-sm font-semibold hover:bg-amber-400 disabled:opacity-60">
              {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sync Products
            </button>
          )}
        </div>

        <div className="space-y-4">
          <Field label="Store Domain">
            <input value={domain} onChange={(e) => setDomain(e.target.value)} data-testid="settings-shopify-domain-input"
              placeholder="your-store.myshopify.com"
              className="w-full bg-transparent outline-none text-sm text-slate-100 placeholder:text-slate-600 font-mono-num" />
          </Field>
          <Field label={`Client ID — API key ${connected ? "(saved — leave blank to keep)" : ""}`}>
            <input value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="settings-shopify-client-id-input"
              placeholder="e.g. 63078909e37c5f2cff1474baa73dd36d"
              className="w-full bg-transparent outline-none text-sm text-slate-100 placeholder:text-slate-600 font-mono-num" />
          </Field>
          <Field label={`Client Secret — API secret key ${connected ? "(saved — leave blank to keep)" : ""}`}>
            <input type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} data-testid="settings-shopify-client-secret-input"
              placeholder="shpss_..."
              className="w-full bg-transparent outline-none text-sm text-slate-100 placeholder:text-slate-600 font-mono-num" />
          </Field>
          <Field label="Webhook signing secret (optional — defaults to your Client Secret)">
            <input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} data-testid="settings-shopify-secret-input"
              placeholder="Leave blank to use your Client Secret"
              className="w-full bg-transparent outline-none text-sm text-slate-100 placeholder:text-slate-600 font-mono-num" />
          </Field>
          <Field label="Location ID (optional — auto-detected)">
            <input value={locationId} onChange={(e) => setLocationId(e.target.value)}
              placeholder="auto"
              className="w-full bg-transparent outline-none text-sm text-slate-100 placeholder:text-slate-600 font-mono-num" />
          </Field>
        </div>

        <div className="flex gap-3 mt-5">
          <button onClick={connect} disabled={connecting} data-testid="settings-save-button"
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500 text-[#04120c] font-semibold text-sm hover:bg-emerald-400 disabled:opacity-60">
            {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} {connected ? "Reconnect" : "Connect Shopify"}
          </button>
          {connected && (
            <button onClick={disconnect} className="flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm text-rose-400 hover:bg-rose-500/10" style={{ borderColor: "var(--pk-border)" }}>
              <Unlink className="h-4 w-4" /> Disconnect
            </button>
          )}
        </div>

        <div className="mt-6 rounded-xl border p-4" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400"><Info className="h-3.5 w-3.5" /> Live order sync</div>
            {connected && (
              <span data-testid="shopify-webhook-status" className={`text-[11px] font-semibold flex items-center gap-1.5 ${settings?.shopify?.webhook_registered ? "text-emerald-400" : "text-amber-400"}`}>
                <span className={`h-2 w-2 rounded-full ${settings?.shopify?.webhook_registered ? "bg-emerald-400" : "bg-amber-400"}`} />
                {settings?.shopify?.webhook_registered ? "Webhook active" : "Webhook not registered"}
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 leading-relaxed mb-3">
            When you connect, we automatically register an <span className="text-slate-300">Order payment</span> webhook with your store, so new paid Shopify orders instantly decrement matching inventory here. We mint &amp; auto-refresh your Admin API token from your Client ID + Client Secret — no manual token re-pasting ever. Your Client Secret also verifies webhook HMAC signatures.
          </p>
          {connected && webhookUrl && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg border mb-3" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
              <code className="text-xs text-cyan-400 font-mono-num truncate flex-1">{webhookUrl}</code>
              <button onClick={() => { navigator.clipboard.writeText(webhookUrl); toast.success("Copied"); }} className="text-slate-400 hover:text-slate-100"><Copy className="h-3.5 w-3.5" /></button>
            </div>
          )}
          {connected && (
            <button onClick={reRegister} disabled={registering} data-testid="shopify-reregister-webhook"
              className="flex items-center gap-2 px-3 py-2 rounded-lg border text-xs text-slate-300 hover:bg-[var(--pk-hover)] disabled:opacity-60" style={{ borderColor: "var(--pk-border)" }}>
              {registering ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Re-register webhook
            </button>
          )}
        </div>

        {connected && (
          <div className="mt-4 rounded-xl border p-4" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }} data-testid="shopify-health-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400"><Activity className="h-3.5 w-3.5" /> Sync health</div>
              <button onClick={ping} disabled={pinging} data-testid="shopify-ping-button"
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs text-slate-200 hover:bg-[var(--pk-hover)] disabled:opacity-60" style={{ borderColor: "var(--pk-border)" }}>
                {pinging ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Activity className="h-3.5 w-3.5" />} Test connection
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Health label="Last paid order received" value={settings?.shopify?.last_webhook_at} />
              <Health label="Last test ping" value={settings?.shopify?.last_ping_at} />
              <Health label="Last product sync" value={settings?.shopify?.last_sync} />
              <Health label="Last push-up" value={settings?.shopify?.last_pushup} />
            </div>
            <button onClick={pushUp} disabled={pushing} data-testid="shopify-pushup-button"
              className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm font-semibold text-slate-200 hover:bg-[var(--pk-hover)] disabled:opacity-60" style={{ borderColor: "var(--pk-border)" }}>
              {pushing ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Push unlisted inventory to Shopify
            </button>
            <p className="text-[11px] text-slate-500 mt-2">Creates Shopify listings (with price, SKU &amp; stock) for opted-in in-stock cards not yet on Shopify, in batches of 50.</p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button onClick={listAll} disabled={listingAll} data-testid="shopify-list-all-button"
                className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-emerald-500 text-[#04120c] text-sm font-semibold hover:bg-emerald-400 disabled:opacity-60">
                {listingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingBag className="h-4 w-4" />} List ALL inventory on Shopify
              </button>
              <button onClick={pushQuantities} disabled={qtySyncing} data-testid="shopify-push-qty-button"
                className="flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm font-semibold text-slate-200 hover:bg-[var(--pk-hover)] disabled:opacity-60" style={{ borderColor: "var(--pk-border)" }}>
                {qtySyncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sync quantities &amp; prices &rarr; Shopify
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mt-2"><span className="text-slate-300">List ALL</span> opts-in every in-stock card and lists it. <span className="text-slate-300">Sync quantities</span> pushes your current stock counts &amp; prices up to Shopify for already-linked cards (use it to fix items showing out of stock).</p>
            <button onClick={backfillCollections} disabled={backfilling} data-testid="shopify-backfill-collections-button"
              className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm font-semibold text-slate-200 hover:bg-[var(--pk-hover)] disabled:opacity-60" style={{ borderColor: "var(--pk-border)" }}>
              {backfilling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Tags className="h-4 w-4" />} Group listings into set collections
            </button>
            <p className="text-[11px] text-slate-500 mt-2">Tags every Shopify listing by set, game, condition &amp; type, and creates a smart collection per set &amp; per game (auto-filled) so you can build store menus and sort/filter cards. New listings are grouped automatically.</p>
            <button onClick={buildMenu} disabled={buildingMenu} data-testid="shopify-build-menu-button"
              className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm font-semibold text-slate-200 hover:bg-[var(--pk-hover)] disabled:opacity-60" style={{ borderColor: "var(--pk-border)" }}>
              {buildingMenu ? <Loader2 className="h-4 w-4 animate-spin" /> : <Menu className="h-4 w-4" />} Build "Shop by Set" navigation menu
            </button>
            <p className="text-[11px] text-slate-500 mt-2">Creates a navigation menu with a dropdown per game, each listing its set collections. After building, assign it in Shopify → Online Store → Navigation. Requires the <span className="text-slate-300">write_online_store_navigation</span> scope on your custom app.</p>
            <button onClick={dedupe} disabled={deduping} data-testid="shopify-dedupe-button"
              className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-rose-500/40 bg-rose-500/10 text-rose-300 font-semibold text-sm hover:bg-rose-500/20 disabled:opacity-60">
              {deduping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlink className="h-4 w-4" />} Remove duplicate listings
            </button>
            <p className="text-[11px] text-slate-500 mt-2">Scans your store for duplicate products (same SKU or title) and deletes the extras, keeping one per card. Run <span className="text-slate-300">Sync quantities</span> afterward to reconcile stock.</p>
            <div className="mt-4 pt-4 border-t flex items-start justify-between gap-4" style={{ borderColor: "var(--pk-border)" }} data-testid="shopify-autolist-row">
              <div>
                <div className="text-sm font-semibold text-slate-200">Auto-list new cards</div>
                <p className="text-[11px] text-slate-500 mt-0.5 max-w-sm">When on, every card you add (Inventory, Buying, Trade) is automatically created as a Shopify listing — no manual push needed. Bulk CSV imports still use the button above.</p>
              </div>
              <PillSwitch checked={autolist} onChange={toggleAutolist} color="emerald" testid="shopify-autolist-toggle" />
            </div>
          </div>
        )}

        <div className="mt-4 rounded-xl border p-4" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }} data-testid="shopify-setup-guide">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">Step-by-step: create your Shopify app</div>
          <ol className="text-xs text-slate-400 leading-relaxed list-decimal ml-4 space-y-2">
            <li>In your Shopify admin, go to <span className="text-slate-200">Settings</span> (bottom-left) → <span className="text-slate-200">Apps and sales channels</span> → <span className="text-slate-200">Develop apps</span>. (If prompted, click <span className="text-slate-200">Allow custom app development</span>.)</li>
            <li>Click <span className="text-slate-200">Create an app</span>, name it <span className="text-slate-200">TCG Vendor Hub</span>, and create it.</li>
            <li>Open the <span className="text-slate-200">Configuration</span> tab → under <span className="text-slate-200">Admin API integration</span> click <span className="text-slate-200">Configure</span>, then enable these scopes and Save:
              <div className="mt-1 flex flex-wrap gap-1.5">
                {["read_products", "write_products", "read_inventory", "write_inventory", "read_orders", "read_locations"].map((s) => (
                  <span key={s} className="px-1.5 py-0.5 rounded bg-slate-700/40 text-slate-300 font-mono-num text-[10px]">{s}</span>
                ))}
              </div>
            </li>
            <li>Go to the <span className="text-slate-200">API credentials</span> tab → click <span className="text-slate-200">Install app</span> (top-right) → <span className="text-slate-200">Install</span>.</li>
            <li>In the <span className="text-slate-200">Client credentials</span> section, copy the <span className="text-slate-200">API key</span> (also called <span className="text-slate-200">Client ID</span>, a long letters-and-numbers value) → paste into <span className="text-slate-200">Client ID</span> above.</li>
            <li>Copy the <span className="text-slate-200">API secret key</span> (a.k.a. <span className="text-slate-200">Client secret</span>, starts with <span className="font-mono-num text-slate-300">shpss_</span>) → paste into <span className="text-slate-200">Client Secret</span> above.</li>
            <li>Enter your <span className="text-slate-200">Store Domain</span> (e.g. <span className="font-mono-num text-slate-300">your-store.myshopify.com</span>) above and click <span className="text-emerald-400">Connect Shopify</span>. We mint your access token automatically and register the paid-order webhook for you.</li>
            <li>Finally, click <span className="text-amber-400">Sync Products</span> above to pull your listings into inventory.</li>
          </ol>
          <p className="text-[11px] text-slate-500 mt-3 pt-3 border-t" style={{ borderColor: "var(--pk-border)" }}>
            Your store uses Shopify's newer app flow, so you won't see a one-time <span className="font-mono-num text-slate-300">shpat_</span> token — just the <span className="text-slate-300">Client ID</span> and <span className="text-slate-300">Client Secret</span> (<span className="font-mono-num text-slate-300">shpss_</span>). That's all we need. Not seeing "Develop apps"? Ask your store owner to enable custom app development.
          </p>
        </div>
      </div>

      <ImportMappingCard />

      {connected && (
        <div className="rounded-2xl border overflow-hidden mt-6" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }} data-testid="shopify-audit-log">
          <div className="px-5 py-4 border-b flex items-center gap-2" style={{ borderColor: "var(--pk-border)" }}>
            <UploadCloud className="h-4 w-4 text-cyan-400" />
            <h2 className="font-heading font-bold text-slate-100">Shopify sync log</h2>
            <span className="text-[11px] text-slate-500 ml-auto">Pushes, imports, orders &amp; quantity syncs — failed items flagged</span>
          </div>
          {audit.length === 0 ? (
            <div className="py-12 text-center text-slate-500"><UploadCloud className="h-8 w-8 mx-auto mb-3 text-slate-700" />No Shopify operations logged yet.</div>
          ) : (
            <div className="divide-y" style={{ borderColor: "var(--pk-border-subtle)" }}>
              {audit.map((a) => <AuditEntry key={a.id} a={a} onRetry={retryFailed} />)}
            </div>
          )}
        </div>
      )}

      <div className="rounded-2xl border overflow-hidden mt-6" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
        <div className="px-5 py-4 border-b flex items-center gap-2" style={{ borderColor: "var(--pk-border)" }}>
          <Activity className="h-4 w-4 text-cyan-400" />
          <h2 className="font-heading font-bold text-slate-100">Recent sync activity</h2>
        </div>
        {synced.length === 0 ? (
          <div className="py-16 text-center text-slate-500"><RefreshCw className="h-8 w-8 mx-auto mb-3 text-slate-700" />No cross-channel syncs yet.</div>
        ) : (
          <>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm" data-testid="sync-activity-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b" style={{ borderColor: "var(--pk-border)" }}>
                  <th className="px-5 py-3 font-semibold">Card</th><th className="px-5 py-3 font-semibold">Sold on</th>
                  <th className="px-5 py-3 font-semibold">Qty</th><th className="px-5 py-3 font-semibold">Synced to</th><th className="px-5 py-3 font-semibold">When</th>
                </tr>
              </thead>
              <tbody>
                {synced.slice(0, 100).map((s) => (
                  <tr key={s.id} data-testid="sync-activity-row" className="border-b hover:bg-[var(--pk-hover)]" style={{ borderColor: "var(--pk-border-subtle)" }}>
                    <td className="px-5 py-3 text-slate-100">{s.card_name}</td>
                    <td className="px-5 py-3 text-slate-300 capitalize">{s.platform}</td>
                    <td className="px-5 py-3 font-mono-num text-slate-300">{s.quantity}</td>
                    <td className="px-5 py-3"><span className="inline-flex items-center gap-1 text-xs text-cyan-400"><RefreshCw className="h-3 w-3" /> {s.synced_platforms.join(", ")}</span></td>
                    <td className="px-5 py-3 text-slate-500 text-xs">{new Date(s.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="md:hidden divide-y" style={{ borderColor: "var(--pk-border-subtle)" }}>
            {synced.slice(0, 100).map((s) => (
              <div key={s.id} data-testid="sync-activity-row" className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-slate-100 font-medium truncate">{s.card_name}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5 capitalize">Sold on {s.platform} · {new Date(s.created_at).toLocaleDateString()}</div>
                  </div>
                  <span className="shrink-0 font-mono-num text-sm text-slate-300">×{s.quantity}</span>
                </div>
                <div className="inline-flex items-center gap-1 text-xs text-cyan-400 mt-2"><RefreshCw className="h-3 w-3" /> Synced to {s.synced_platforms.join(", ")}</div>
              </div>
            ))}
          </div>
          </>
        )}
      </div>
    </div>
  );
}

function Health({ label, value }) {
  const t = value ? new Date(value).toLocaleString() : "—";
  return (
    <div className="rounded-lg border px-3 py-2" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-xs text-slate-200 mt-0.5 truncate">{t}</div>
    </div>
  );
}

function ImportMappingCard() {
  const [data, setData] = useState(null);
  const [field, setField] = useState("tags");
  const [defaultGame, setDefaultGame] = useState("pokemon");
  const [rules, setRules] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/shopify/import-map").then(({ data }) => {
      setData(data); setField(data.field); setDefaultGame(data.default_game); setRules(data.rules || []);
    }).catch(() => {});
  }, []);

  const addRule = () => setRules((r) => [...r, { value: "", game: data?.games?.[0]?.id || "pokemon" }]);
  const upd = (i, k, v) => setRules((r) => r.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const del = (i) => setRules((r) => r.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const { data: res } = await api.post("/settings/shopify/import-map", {
        field, default_game: defaultGame, rules: rules.filter((r) => r.value.trim()),
      });
      setRules(res.rules);
      toast.success("Import mapping saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!data) return null;
  const games = data.games || [];

  return (
    <div className="rounded-2xl border p-6 mt-6" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }} data-testid="settings-import-map-card">
      <h2 className="font-heading text-lg font-bold text-slate-100 mb-1">Shopify import mapping</h2>
      <p className="text-xs text-slate-500 mb-4">Auto-categorize cards imported from Shopify by matching a product field to a game.</p>

      <div className="flex flex-wrap gap-4 mb-4">
        <label className="text-sm">
          <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Match on field</span>
          <select value={field} onChange={(e) => setField(e.target.value)} data-testid="import-map-field"
            className="px-3 py-2 rounded-lg border bg-transparent text-slate-100 outline-none" style={{ borderColor: "var(--pk-border)" }}>
            {data.fields.map(([v, l]) => <option key={v} value={v} className="bg-[#181D2A]">{l}</option>)}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Default game (no match)</span>
          <select value={defaultGame} onChange={(e) => setDefaultGame(e.target.value)} data-testid="import-map-default"
            className="px-3 py-2 rounded-lg border bg-transparent text-slate-100 outline-none" style={{ borderColor: "var(--pk-border)" }}>
            {games.map((g) => <option key={g.id} value={g.id} className="bg-[#181D2A]">{g.label}</option>)}
          </select>
        </label>
      </div>

      <div className="space-y-2">
        {rules.map((r, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`import-rule-${i}`}>
            <span className="text-xs text-slate-500 w-24">if {field} contains</span>
            <input value={r.value} onChange={(e) => upd(i, "value", e.target.value)} data-testid={`import-rule-value-${i}`}
              placeholder="e.g. MTG" className="flex-1 px-3 py-2 rounded-lg border bg-transparent text-slate-100 outline-none text-sm" style={{ borderColor: "var(--pk-border)" }} />
            <span className="text-xs text-slate-500">→</span>
            <select value={r.game} onChange={(e) => upd(i, "game", e.target.value)} data-testid={`import-rule-game-${i}`}
              className="px-3 py-2 rounded-lg border bg-transparent text-slate-100 outline-none text-sm" style={{ borderColor: "var(--pk-border)" }}>
              {games.map((g) => <option key={g.id} value={g.id} className="bg-[#181D2A]">{g.label}</option>)}
            </select>
            <button onClick={() => del(i)} data-testid={`import-rule-remove-${i}`} className="text-slate-500 hover:text-rose-400 text-xs px-2">Remove</button>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 mt-4">
        <button onClick={addRule} data-testid="import-map-add-rule"
          className="px-3 py-2 rounded-lg border text-sm text-slate-300 hover:bg-[var(--pk-hover)]" style={{ borderColor: "var(--pk-border)" }}>+ Add rule</button>
        <button onClick={save} disabled={saving} data-testid="import-map-save"
          className="flex items-center gap-2 px-5 py-2 rounded-xl bg-amber-500 text-[#1a1205] font-semibold text-sm hover:bg-amber-400 disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Save Mapping
        </button>
      </div>
    </div>
  );
}

function AuditEntry({ a }) {
  const [open, setOpen] = useState(false);
  const LABELS = {
    push: "Listed products", import: "Imported from Shopify", order: "Order received",
    push_quantities: "Synced quantities & prices", collections_backfill: "Grouped into collections",
    menu_build: "Built navigation menu",
  };
  const label = LABELS[a.action] || a.action;
  const parts = Object.entries(a.counts || {}).filter(([k]) => k !== "failed").map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`);
  const hasFail = (a.failed || 0) > 0 || (a.item_errors || []).length > 0;
  return (
    <div className="px-5 py-3" data-testid="audit-entry">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-slate-100 font-medium text-sm">{label}</span>
            {hasFail ? (
              <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30" data-testid="audit-failed-badge">{a.failed} failed</span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"><CheckCircle2 className="h-3 w-3" /> OK</span>
            )}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">{parts.join(" · ") || "no changes"}</div>
        </div>
        <div className="text-[11px] text-slate-500 shrink-0 text-right">
          {a.created_at ? new Date(a.created_at).toLocaleString() : ""}
          {hasFail && (
            <button onClick={() => setOpen((v) => !v)} data-testid="audit-toggle-failed"
              className="block mt-1 text-rose-300 hover:text-rose-200 underline">{open ? "Hide" : "View failed"}</button>
          )}
          {hasFail && RETRYABLE.has(a.action) && (
            <button onClick={() => onRetry(a.action)} data-testid="audit-retry"
              className="inline-flex items-center gap-1 mt-1 text-cyan-300 hover:text-cyan-200 underline"><RefreshCw className="h-3 w-3" /> Retry failed</button>
          )}
        </div>
      </div>
      {open && hasFail && (
        <div className="mt-2 rounded-lg border border-rose-500/25 bg-rose-500/5 p-3 space-y-1.5" data-testid="audit-failed-list">
          {(a.item_errors || []).map((it, i) => (
            <div key={i} className="text-[11px]">
              <span className="text-rose-200 font-medium">{it.name}</span>
              <span className="text-slate-400"> — {it.error}</span>
            </div>
          ))}
          {(a.item_errors || []).length === 0 && (a.errors || []).map((e, i) => (
            <div key={i} className="text-[11px] text-slate-400">{e}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</span>
      <div className="mt-1.5 px-3 py-2.5 rounded-xl border focus-within:ring-2 focus-within:ring-amber-500/50 transition-all" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
        {children}
      </div>
    </label>
  );
}
